/**
 * Small summary card for a grid listing: icon + title/subtitle, optional
 * top-right tags, optional description and footer. Used wherever a list of
 * entities (agents, MCP servers, ...) links each item to its own detail view.
 */
import React from 'react';
import { Card, Typography } from 'antd';
import { colors } from '../../theme';

const { Title, Text, Paragraph } = Typography;

export interface EntityCardProps {
  icon: React.ReactNode;
  iconBackground?: string;
  iconColor?: string;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Top-right area, e.g. status/category tags. */
  tags?: React.ReactNode;
  /** Ellipsized to 2 lines; fills remaining height so footer sticks to the bottom. */
  description?: React.ReactNode;
  footer?: React.ReactNode;
  onClick?: () => void;
}

const EntityCard: React.FC<EntityCardProps> = ({
  icon,
  iconBackground = colors.primarySoft,
  iconColor = colors.primary,
  title,
  subtitle,
  tags,
  description,
  footer,
  onClick,
}) => (
  <Card
    hoverable
    bordered={false}
    onClick={onClick}
    style={{ borderRadius: 10, height: '100%', transition: 'box-shadow 0.3s' }}
    styles={{ body: { display: 'flex', flexDirection: 'column', height: '100%', padding: 16 } }}
  >
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8, gap: 8 }}>
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: iconBackground,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 16,
          color: iconColor,
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      {tags}
    </div>

    <Title level={5} style={{ margin: '0 0 2px 0', fontSize: 14 }}>
      {title}
    </Title>
    {subtitle && (
      <Text type="secondary" style={{ fontSize: 11, marginBottom: 6, display: 'block' }}>
        {subtitle}
      </Text>
    )}

    {description && (
      <Paragraph type="secondary" ellipsis={{ rows: 2 }} style={{ marginBottom: 8, fontSize: 12, flex: 1 }}>
        {description}
      </Paragraph>
    )}

    {footer}
  </Card>
);

export default EntityCard;
